import * as React from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Button, Checkbox, FormControlLabel, MenuItem, TextField, Tooltip } from '@mui/material';
import { Feature } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Subject } from 'rxjs';
import { colorPalette } from '@qualifio/ui-commons';
import {
  customerDomainSchema,
  isSecondLevelDomain,
} from '@loyalty/shared-common/gamification-api-client';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { makeStyles } from '@mui/styles';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DomainTableRow } from './DomainTableRow';
import { useAccounts } from '../../accounts/AccountsContext';
import { useApi } from '../../shared/api-client/ApiClientProvider';
import { useDomains } from '../SubdomainsContext';
import { useFeatures } from '../../features/FeaturesContext';
import { usePrograms } from '../../loyalty/ProgramsContext';

const useStyles = makeStyles(() => ({
  lastRow: {
    verticalAlign: 'top !important',
    '&:last-child td, &:last-child th': { border: 0 },
  },
  programSelect: {
    height: '40px',
  },
  verificationZone: {
    color: colorPalette.grey.dark,
    padding: '10px',
    textAlign: 'justify',
  },
  iconButton: {
    fontSize: '16px',
    color: colorPalette.grey.main,
    backgroundColor: 'inherit',
    minWidth: 40,
    '&:hover': {
      backgroundColor: 'inherit',
      color: colorPalette.grey.darkHover,
    },
  },
  deleteButton: {
    '&:hover': {
      color: colorPalette.red.main,
    },
  },
  domainCell: {
    width: '20%',
  },
  programCell: {
    width: '15%',
  },
  statusCell: {
    width: '55%',
  },
  buttonCell: {
    width: '5%',
  },
  deleteCell: {
    width: '5%',
  },
}));

export function DomainTable() {
  const { subdomains, isLoading, addSubdomain } = useDomains();
  const { t } = useTranslation();
  const { programs } = usePrograms();
  const { gamificationApi } = useApi();
  const classes = useStyles();
  const [programId, setProgramId] = useState('');
  const [domainUrl, setDomainUrl] = useState('');
  const [addAsDevelopmentDomain, setAddAsDevelopmentDomain] = useState(false);
  const { currentAccount } = useAccounts();
  const { current: search$ } = useRef(new Subject<string>());
  const [isDuplicated, setIsDuplicated] = useState(false);
  const { features } = useFeatures();
  const { write: subdomainWrite } = features[Feature.DOMAINS_MANAGEMENT];
  const { enabled: devDomainEnabled } = features[Feature.DEV_DOMAIN];

  useEffect(() => {
    const subscription = search$
      .pipe(
        debounceTime(300),
        distinctUntilChanged((a, b) => a === b),
      )
      .subscribe(value => {
        if (!value.trim()) {
          setIsDuplicated(false);
          return;
        }
        gamificationApi
          .verifySubdomainDuplication(currentAccount.id, value)
          .then(setIsDuplicated)
          .catch(console.error);
      });
    return () => {
      subscription.unsubscribe();
    };
  }, [currentAccount.id, gamificationApi]);

  function isValidHostname(domainUrl: string) {
    return !customerDomainSchema.allow('').validate(domainUrl).error;
  }

  const secondLevelRequirementOk = addAsDevelopmentDomain || isSecondLevelDomain(domainUrl);
  const duplicationOk = !isDuplicated;
  const domainFieldError =
    !isValidHostname(domainUrl) || !duplicationOk || !secondLevelRequirementOk;

  const canSaveNewDomain =
    subdomainWrite &&
    !isLoading('addSubdomain') &&
    !!domainUrl &&
    !!programId &&
    isValidHostname(domainUrl) &&
    secondLevelRequirementOk &&
    duplicationOk;

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell> {t('subdomain.edition.table.name')}</TableCell>
            <TableCell> {t('subdomain.edition.table.program')}</TableCell>
            <TableCell> {t('subdomain.edition.table.status')} </TableCell>
            <TableCell />
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {subdomains
            .filter(domain => !domain.deletedAt)
            .map(subdomain => {
              return <DomainTableRow key={subdomain.id} subdomain={subdomain} />;
            })}
          <TableRow key={'newDomain'} className={classes.lastRow}>
            <TableCell scope="row" className={classes.domainCell}>
              <TextField
                error={domainFieldError}
                helperText={
                  isDuplicated
                    ? t('subdomain.edition.error.duplication')
                    : !isValidHostname(domainUrl)
                    ? t('subdomain.edition.error.special.chars')
                    : !secondLevelRequirementOk
                    ? t('subdomain.edition.error.second-level')
                    : ''
                }
                value={domainUrl}
                placeholder={t('subdomain.placeholder.domain')}
                onChange={e => {
                  setDomainUrl(e.target.value);
                  search$.next(e.target.value);
                }}
              />
              {devDomainEnabled ? (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={addAsDevelopmentDomain}
                      onChange={e => setAddAsDevelopmentDomain(e.target.checked)}
                    />
                  }
                  label={t('subdomain.edition.development.mode.toggle')}
                />
              ) : null}
            </TableCell>
            <TableCell align="left" className={classes.programCell}>
              <TextField
                select
                className={classes.programSelect}
                value={programId}
                onChange={e => setProgramId(e.target.value)}
                placeholder={t('subdomain.placeholder.program')}
              >
                {programs.map(program => (
                  <MenuItem key={program.name} value={program.id}>
                    {program.name}
                  </MenuItem>
                ))}
              </TextField>
            </TableCell>
            <TableCell align="left" className={classes.statusCell}>
              <div className={classes.verificationZone}>
                {addAsDevelopmentDomain
                  ? t('subdomain.edition.development.mode.new.row.hint')
                  : !domainUrl || isDuplicated || !programId
                  ? ''
                  : t('subdomain.verification.empty')}
              </div>
            </TableCell>
            <TableCell align="right" className={classes.buttonCell}>
              <Tooltip title={subdomainWrite ? '' : t('shared.action.permission.required')}>
                <div>
                  <Button
                    startIcon={
                      <FontAwesomeIcon
                        fontSize="inherit"
                        icon={isLoading('addSubdomain') ? faSpinner : faCheck}
                        spin={isLoading('addSubdomain')}
                      />
                    }
                    color="success"
                    onClick={() => {
                      addSubdomain(currentAccount.id, {
                        domain: domainUrl,
                        programId,
                        developmentMode: addAsDevelopmentDomain,
                      });
                      setDomainUrl('');
                      setProgramId('');
                      setAddAsDevelopmentDomain(false);
                    }}
                    disabled={!canSaveNewDomain}
                  >
                    {t('shared.actions.save')}
                  </Button>
                </div>
              </Tooltip>
            </TableCell>
            <TableCell align="right" className={classes.deleteCell}></TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}
